/*
==============================================================================
MANTRAROMA CUSTOM
Version: 1.0.1
Datum: 16.09.2026
Zweck: Kennzeichnet alle ueber [KI] markierten Inhalts- und Produktkartenbilder.
      Bestehende Kennzeichnungen der Produkt- und Zoomgalerie bleiben unberuehrt.
      Bilder im Variantenpicker werden nicht gekennzeichnet.
==============================================================================
*/

(() => {
  const imageSelector = 'img[data-mantraroma-ai-image="true"]';
  const labelClass = 'mantraroma-ai-image-label--global';
  const labels = new Map();

  const excludedSelector = [
    '.product-media--image',
    '.gallery-viewer',
    '.thumbnail',
    'variant-picker',
    '.product-block-options',
    '.mantraroma-stone-picker',
    '.mantraroma-category-picker',
    '.cart-item'
  ].join(',');

  const findHost = (image) => {
    const mediaElement = image.parentElement?.tagName === 'PICTURE'
      ? image.parentElement
      : image;
    const linkedMedia = mediaElement.closest('a');
    let host = linkedMedia?.parentElement || mediaElement.parentElement;

    while (
      host &&
      window.getComputedStyle(host).display === 'contents'
    ) {
      host = host.parentElement;
    }

    return host;
  };

  const positionLabel = (image, label, host) => {
    if (!image.isConnected || !label.isConnected || !host.isConnected) {
      return;
    }

    const imageRect = image.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const spacing = window.matchMedia('(max-width: 767px)').matches ? 6 : 8;

    label.style.setProperty(
      '--mantraroma-ai-label-x',
      `${imageRect.right - hostRect.left - spacing}px`
    );
    label.style.setProperty(
      '--mantraroma-ai-label-y',
      `${imageRect.bottom - hostRect.top - spacing}px`
    );
  };

  const removeLabel = (image) => {
    const entry = labels.get(image);

    if (!entry) return;

    entry.observer.disconnect();
    entry.label.remove();
    labels.delete(image);

    if (!entry.host.querySelector(`.${labelClass}`)) {
      entry.host.classList.remove('mantraroma-ai-image-global-host');
    }
  };

  const addLabel = (image) => {
    if (
      labels.has(image) ||
      image.closest(excludedSelector)
    ) {
      return;
    }

    const host = findHost(image);

    if (!host) return;

    const label = document.createElement('a');
    label.className = `mantraroma-ai-image-label ${labelClass}`;
    label.href = '/pages/so-entstehen-unsere-bilder';
    label.setAttribute(
      'aria-label',
      'Dieses Bild wurde mit künstlicher Intelligenz erstellt. Mehr über die Bilderstellung erfahren.'
    );
    label.innerHTML = [
      '<span class="mantraroma-ai-image-label__short" aria-hidden="true">KI-generiert</span>',
      '<span class="mantraroma-ai-image-label__detail" aria-hidden="true">Bild mit KI erstellt – mehr erfahren</span>'
    ].join('');

    host.classList.add('mantraroma-ai-image-global-host');
    host.append(label);

    const observer = new ResizeObserver(() => {
      positionLabel(image, label, host);
    });

    observer.observe(image);
    observer.observe(host);
    labels.set(image, { label, host, observer });

    image.addEventListener(
      'load',
      () => positionLabel(image, label, host),
      { passive: true }
    );

    requestAnimationFrame(() => positionLabel(image, label, host));
  };

  const syncImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const altText = image.getAttribute('alt') || '';

    if (altText.includes('[KI]')) {
      image.dataset.mantraromaAiImage = 'true';
      image.setAttribute(
        'alt',
        altText.replace(/\[KI\]\s*/g, '').trim()
      );
    }

    if (image.matches(imageSelector)) {
      addLabel(image);
    } else {
      removeLabel(image);
    }
  };

  const syncAll = (root = document) => {
    if (root instanceof HTMLImageElement) {
      syncImage(root);
    }

    root.querySelectorAll?.('img').forEach(syncImage);
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        syncImage(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) syncAll(node);
      });

      mutation.removedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node instanceof HTMLImageElement) removeLabel(node);
        node.querySelectorAll?.('img').forEach(removeLabel);
      });
    });
  });

  syncAll();

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-mantraroma-ai-image', 'alt']
  });

  window.addEventListener('resize', () => {
    labels.forEach(({ label, host }, image) => {
      positionLabel(image, label, host);
    });
  });
})();
